<!-- eslint-disable vue/require-prop-types -->
<template>
  <slot v-if="props.withTriggerBtn" name="trigger" :open="handleOpenButtonClick">
    <etg-button
      :class="[{ active: triggerActive }, props.triggerClass]"
      size="small"
      :type="triggerBtnType"
      :icon="props.triggerIcon"
      @click="handleOpenButtonClick"
    >
      {{ props.triggerLabel }}
    </etg-button>
  </slot>
  <n-drawer
    v-model:show="visible"
    class="bottom-drawer"
    placement="bottom"
    :z-index="props.zIndex"
    :display-directive="props.displayDirective"
    v-bind="{ height: 'auto', ...$attrs }"
    :style="customCssVars"
    @mask-click="handleClose"
    @after-enter="handleAfterEnter"
    @after-leave="handleAfterLeave"
  >
    <n-drawer-content v-bind="props.drawerContentProps">
      <template #header>
        <slot name="header">
          <div class="flex justify-between items-center px-3 py-2">
            <div ref="actionRef" class="action flex items-center min-w-10 max-w-105px">
              <etg-button v-if="props.isActionShown" class="active" type="text" size="small" :truncate-text="true" @click="handleActionClick">
                {{ props.actionLabel }}
              </etg-button>
            </div>
            <div class="flex flex-1 justify-center items-center">
              <span class="line-clamp-1 text-lg">{{ props.title }}</span>
            </div>
            <div class="flex justify-end items-center" :style="cancelStyle">
              <svg-icon name="cancel" class="cursor-pointer w-10 h-10 p-2" @click="handleClose" />
            </div>
          </div>
        </slot>
      </template>
      <template #default>
        <div class="content">
          <slot :close="handleClose"></slot>
        </div>
      </template>
      <template #footer>
        <slot name="footer">
          <div v-if="props.isFooterShown" class="drawer-footer w-full py-3 px-5">
            <etg-button type="primary" class="w-full" :loading="props.isLoading" :disabled="!props.isSubmittable" @click="handleSubmit">
              {{ props.submitLabel }}
            </etg-button>
          </div>
        </slot>
      </template>
    </n-drawer-content>
  </n-drawer>
</template>

<script setup lang="ts">
import { NDrawer, NDrawerContent } from 'naive-ui'

import { zIndex as zIndexConfig } from '@/theme.config'

import type { IconName } from '@/types/icon'
import type { EtgButtonProps, NDrawerProps, NDrawerContentProps } from '@/types/component'

interface Props {
  title?: string
  triggerClass?: string
  triggerLabel?: string
  triggerIcon?: IconName
  triggerActive?: boolean
  isActionShown?: boolean
  isFooterShown?: boolean
  actionLabel?: string
  submitLabel?: string
  isSubmittable?: boolean
  isLoading?: boolean
  withTriggerBtn?: boolean
  triggerBtnType?: EtgButtonProps['type']
  drawerContentProps?: NDrawerContentProps
  zIndex?: number
  displayDirective?: NDrawerProps['displayDirective']
  drawerBodyMaxHeight?: string | number
}

interface Emits {
  (event: 'action'): void
  (event: 'submit'): void
  (event: 'close'): void
  (event: 'after-enter'): void
  (event: 'after-leave'): void
}

const visible = defineModel<boolean>('visible', { default: false })

const props = withDefaults(defineProps<Props>(), {
  title: '',
  triggerLabel: '',
  triggerActive: false,
  actionLabel: () => useI18n().t('filter.clear'),
  isActionShown: false,
  isFooterShown: false,
  submitLabel: () => useI18n().t('general.confirm'),
  isSubmittable: true,
  isLoading: false,
  withTriggerBtn: true,
  triggerBtnType: 'secondary',
  zIndex: zIndexConfig.drawer,
  displayDirective: 'if',
})

const emit = defineEmits<Emits>()

const actionRef = ref<Nullable<HTMLDivElement>>(null)

const actionBtnSize = useElementSize(actionRef)
const { isInFunNowGroupApp } = useBridge()

const triggerActive = computed(() => visible.value || props.triggerActive)

const customCssVars = computed(() => {
  if (props.drawerBodyMaxHeight) {
    return {
      '--drawer-body-max-height': props.drawerBodyMaxHeight,
    }
  }
  let maxHeight = 128
  if (props.isFooterShown) maxHeight += 64

  return {
    '--drawer-body-max-height':
      // Android webview 無法使用 dvh，目前還不知道為何，先使用 `innerHeight` 計算來解。
      isInFunNowGroupApp.value && import.meta.client ? `${window.innerHeight - maxHeight}px` : `calc(100dvh - ${maxHeight}px)`,
  }
})

const cancelStyle = computed(() => ({ ...(actionBtnSize.width.value && { width: `${actionBtnSize.width.value}px` }) }))

const handleActionClick = () => emit('action')

const handleSubmit = () => {
  visible.value = false
  emit('submit')
}

const handleOpenButtonClick = () => {
  visible.value = true
}

const handleClose = () => {
  visible.value = false
  emit('close')
}

const handleAfterEnter = () => {
  emit('after-enter')
}

const handleAfterLeave = () => {
  emit('after-leave')
}
</script>

<style lang="scss">
.bottom-drawer {
  @apply overflow-hidden rounded-t-12px;
  &.n-drawer.n-drawer--bottom-placement {
    .n-drawer-header {
      @apply sticky top-0 bg-white z-10;
      .n-drawer-header__main {
        @apply w-full;
      }
    }
    .n-drawer-body {
      @apply flex-1;
    }
    .n-drawer-body-content-wrapper {
      @apply overflow-y-auto;
      max-height: var(--drawer-body-max-height);

      &:has(.full-height) {
        height: var(--drawer-body-max-height);
      }
    }
    .content {
      &:has(.main-content.h-full) {
        @apply h-full;
      }
    }
    .n-drawer-footer {
      @apply bg-white;
      box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
